import React, { useState, useEffect } from 'react';
import { View, TouchableOpacity, StyleSheet, ImageBackground, Image } from 'react-native';
import { Text } from '@app/design-system';
import { Block } from '../../types/websiteConfig';
import { useWebsiteBuilder } from '../../store/websiteBuilderStore';
import { storageService } from '@app/core';

interface Props {
  block: Block;
  isSelected: boolean;
  onSelect: () => void;
}

// Image Gallery Preview Component
interface ImageGalleryPreviewProps {
  images: any[];
  columns: number;
  gap: number;
  borderRadius: number;
  themeColors: any;
}

const ImageGalleryPreview: React.FC<ImageGalleryPreviewProps> = ({ images, columns, gap, borderRadius, themeColors }) => {
  const [imageUrls, setImageUrls] = useState<{ [key: string]: string }>({});
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set());

  useEffect(() => {
    const loadImages = async () => {
      const newLoadingSet = new Set(loadingImages);
      const newUrls: { [key: string]: string } = { ...imageUrls };

      for (const image of images) {
        const imageId = image.id;
        const blobPath = image.blobPath;

        // Skip if already loaded or loading
        if (imageUrls[imageId] || loadingImages.has(imageId)) {
          continue;
        }

        if (!blobPath) continue;

        newLoadingSet.add(imageId);
        setLoadingImages(newLoadingSet);

        try {
          if (blobPath.startsWith('http')) {
            newUrls[imageId] = blobPath;
          } else {
            const readUrl = await storageService.getReadUrl(blobPath);
            newUrls[imageId] = readUrl;
          }
        } catch (error) {
          console.error('❌ [ImageGalleryPreview] Failed to load image:', imageId, error);
        } finally {
          newLoadingSet.delete(imageId);
        }
      }

      setImageUrls(newUrls);
      setLoadingImages(newLoadingSet);
    };

    if (images && images.length > 0) {
      loadImages();
    }
  }, [images]);

  if (!images || images.length === 0) {
    return null;
  }

  const columnWidth = `${(100 / columns) - ((gap * (columns - 1)) / columns)}%`;

  return (
    <View style={{
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: gap,
    }}>
      {images.map((image, index) => (
        <View
          key={image.id || index}
          style={{
            width: columnWidth,
            aspectRatio: 1,
            borderRadius: borderRadius,
            overflow: 'hidden',
            backgroundColor: themeColors.secondary || '#F5F5F5',
            position: 'relative',
          }}
        >
          {imageUrls[image.id] ? (
            <>
              <img
                src={imageUrls[image.id]}
                alt={image.alt || `Gallery image ${index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              {/* Tag and Caption overlay */}
              {(image.tag || image.caption) && (
                <View style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  paddingVertical: 8,
                  paddingHorizontal: 12,
                }}>
                  {image.tag && (
                    <Text style={{
                      color: '#FFD700',
                      fontSize: 10,
                      fontWeight: '600',
                      textAlign: 'center',
                      marginBottom: image.caption ? 4 : 0,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}>
                      {image.tag}
                    </Text>
                  )}
                  {image.caption && (
                    <Text style={{
                      color: '#FFFFFF',
                      fontSize: 12,
                      textAlign: 'center',
                    }}>
                      {image.caption}
                    </Text>
                  )}
                </View>
              )}
            </>
          ) : (
            <View style={{
              width: '100%',
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: themeColors.secondary || '#F5F5F5',
            }}>
              <Text style={{ fontSize: 32 }}>🖼️</Text>
              {loadingImages.has(image.id) && (
                <Text style={{ fontSize: 10, marginTop: 4, opacity: 0.6 }}>Loading...</Text>
              )}
            </View>
          )}
        </View>
      ))}
    </View>
  );
};

export const BlockRenderer: React.FC<Props> = ({ block, isSelected, onSelect }) => {
  const { config } = useWebsiteBuilder();
  const [backgroundImageUrl, setBackgroundImageUrl] = useState<string | null>(null);
  const [logoImageUrl, setLogoImageUrl] = useState<string | null>(null);

  // Load background image for HERO_BANNER
  useEffect(() => {
    const loadBackgroundImage = async () => {
      if (block.type === 'HERO_BANNER' && block.config.bgImage) {
        const blobPath = block.config.bgImage;
        // Check if it's already a full URL or a blob path
        if (blobPath.startsWith('http')) {
          setBackgroundImageUrl(blobPath);
        } else {
          try {
            console.log('🖼️ [BlockRenderer] Loading background image for HERO_BANNER:', blobPath);
            const readUrl = await storageService.getReadUrl(blobPath);
            console.log('✅ [BlockRenderer] Background image URL loaded:', readUrl);
            setBackgroundImageUrl(readUrl);
          } catch (error) {
            console.error('❌ [BlockRenderer] Failed to load background image:', error);
            setBackgroundImageUrl(null);
          }
        }
      } else {
        setBackgroundImageUrl(null);
      }
    };

    loadBackgroundImage();
  }, [block.type, block.config.bgImage]);

  // Load logo image for HEADER
  useEffect(() => {
    const loadLogoImage = async () => {
      if (block.type === 'HEADER' && block.config.logo) {
        const blobPath = block.config.logo;
        // Check if it's already a full URL or a blob path
        if (blobPath.startsWith('http')) {
          setLogoImageUrl(blobPath);
        } else {
          try {
            console.log('🖼️ [BlockRenderer] Loading logo for HEADER:', blobPath);
            const readUrl = await storageService.getReadUrl(blobPath);
            console.log('✅ [BlockRenderer] Logo URL loaded:', readUrl);
            setLogoImageUrl(readUrl);
          } catch (error) {
            console.error('❌ [BlockRenderer] Failed to load logo:', error);
            setLogoImageUrl(null);
          }
        }
      } else {
        setLogoImageUrl(null);
      }
    };

    loadLogoImage();
  }, [block.type, block.config.logo]);

  // Helper function for platform colors
  const getPlatformColor = (platform: string): string => {
    const colors: Record<string, string> = {
      'Instagram': '#E4405F',
      'Twitter': '#1DA1F2',
      'X': '#000000',
      'Facebook': '#1877F2',
      'YouTube': '#FF0000',
      'LinkedIn': '#0A66C2',
      'TikTok': '#000000',
      'Threads': '#000000',
      'Pinterest': '#E60023',
      'WhatsApp': '#25D366'
    };
    return colors[platform] || '#666666';
  };

  // Helper function for platform icons
  const getPlatformIcon = (platform: string): string => {
    const icons: Record<string, string> = {
      'Instagram': '📷',
      'Twitter': '🐦',
      'X': '✖️',
      'Facebook': '👤',
      'YouTube': '▶️',
      'LinkedIn': '💼',
      'TikTok': '🎵',
      'Threads': '🧵',
      'Pinterest': '📌',
      'WhatsApp': '💬'
    };
    return icons[platform] || '📱';
  };

  // Use theme from website builder config
  const themeColors = {
    primary: config?.theme?.primaryColor || '#34C759',
    secondary: config?.theme?.secondaryColor || '#F5F5F5',
    text: config?.theme?.textColor || '#1D1D1F',
    background: config?.theme?.backgroundColor || '#FFFFFF',
  };
  const accentColor = config?.theme?.accentColor || '#FF9500';
  const borderRadius = config?.theme?.borderRadius || '8px';
  const fontFamily = config?.theme?.fontFamily || 'Poppins, sans-serif';
  const typography = config?.theme?.typography || {
    h1Size: '32px',
    h2Size: '24px',
    h3Size: '20px',
    bodySize: '16px',
    smallSize: '14px',
    h1Weight: '700',
    h2Weight: '600',
    bodyWeight: '400'
  };

  const renderBlockContent = () => {
    switch (block.type) {
      case 'HEADER':
        return (
          <View style={{
            backgroundColor: block.config.bgColor || themeColors.background,
            paddingHorizontal: 24,
            paddingVertical: 12,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: parseInt(block.config.height) || 80,
            borderBottomWidth: 1,
            borderBottomColor: '#E5E5E5'
          }}>
            {/* Center: Logo + Shop Name */}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1, justifyContent: 'center' }}>
              {/* Logo */}
              {logoImageUrl ? (
                <View style={{
                  width: parseInt(block.config.logoHeight) || 60,
                  height: parseInt(block.config.logoHeight) || 60,
                  borderRadius: 8,
                  overflow: 'hidden',
                  backgroundColor: '#F5F5F5'
                }}>
                  <img
                    src={logoImageUrl}
                    alt="Shop Logo"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </View>
              ) : (
                <View style={{
                  width: parseInt(block.config.logoHeight) || 60,
                  height: parseInt(block.config.logoHeight) || 60,
                  borderRadius: 8,
                  backgroundColor: '#E5E5E5',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Text style={{ fontSize: 24 }}>🏪</Text>
                </View>
              )}

              {/* Shop Name */}
              <Text style={{
                fontSize: parseInt(typography.h3Size) || 20,
                fontWeight: '700',
                color: block.config.textColor || themeColors.text,
                fontFamily
              }}>
                {block.config.shopName || 'My Shop'}
              </Text>
            </View>
          </View>
        );

      case 'ANNOUNCEMENT_BAR':
        console.log('🎨 [BlockRenderer] Rendering announcement bar');
        console.log('🎨 [BlockRenderer] Block ID:', block.id);
        console.log('🎨 [BlockRenderer] Full block object:', JSON.stringify(block, null, 2));
        console.log('🎨 [BlockRenderer] Block config:', JSON.stringify(block.config, null, 2));
        console.log('🎨 [BlockRenderer] Using bgColor:', block.config.bgColor || themeColors.primary);
        console.log('🎨 [BlockRenderer] Using textColor:', block.config.textColor || '#FFFFFF');
        console.log('🎨 [BlockRenderer] Theme primary color:', themeColors.primary);
        return (
          <View style={{ backgroundColor: block.config.bgColor || themeColors.primary, padding: 12 }}>
            <Text style={{ color: block.config.textColor || '#FFFFFF', textAlign: 'center', fontSize: parseInt(typography.smallSize) || 14, fontFamily }}>
              {block.config.text || 'Announcement message'}
            </Text>
          </View>
        );

      case 'HERO_BANNER':
        const HeroBannerContent = (
          <View style={{ padding: 48, alignItems: 'center', width: '100%', minHeight: 300, justifyContent: 'center' }}>
            <Text style={{
              fontSize: parseInt(typography.h1Size) || 32,
              fontWeight: typography.h1Weight as any || '700',
              marginBottom: 16,
              textAlign: 'center',
              color: backgroundImageUrl ? '#FFFFFF' : themeColors.text,
              fontFamily,
              textShadowColor: backgroundImageUrl ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
              textShadowOffset: { width: 0, height: 1 },
              textShadowRadius: 4,
            }}>
              {block.config.title || 'Hero Title'}
            </Text>
            <Text style={{
              fontSize: parseInt(typography.bodySize) || 16,
              color: backgroundImageUrl ? '#FFFFFF' : themeColors.text,
              marginBottom: 24,
              textAlign: 'center',
              opacity: 0.9,
              fontFamily,
              textShadowColor: backgroundImageUrl ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
              textShadowOffset: { width: 0, height: 1 },
              textShadowRadius: 4,
            }}>
              {block.config.subtitle || 'Hero subtitle goes here'}
            </Text>

            {/* Location Section */}
            {(block.config.displayLocation ?? false) && (
              <View style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: 16,
                backgroundColor: backgroundImageUrl ? 'rgba(0, 0, 0, 0.3)' : themeColors.secondary,
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: parseInt(borderRadius) || 8
              }}>
                <Text style={{
                  fontSize: parseInt(typography.smallSize) || 14,
                  color: backgroundImageUrl ? '#FFFFFF' : themeColors.text,
                  fontFamily
                }}>
                  📍 Location Section
                </Text>
              </View>
            )}

            {/* Connect Section */}
            {(block.config.displayConnect ?? false) && (
              <View style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: 16,
                backgroundColor: backgroundImageUrl ? 'rgba(0, 0, 0, 0.3)' : themeColors.secondary,
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: parseInt(borderRadius) || 8
              }}>
                <Text style={{
                  fontSize: parseInt(typography.smallSize) || 14,
                  color: backgroundImageUrl ? '#FFFFFF' : themeColors.text,
                  fontFamily
                }}>
                  🔗 Connect Section
                </Text>
              </View>
            )}

            <View style={{ backgroundColor: accentColor, paddingHorizontal: 24, paddingVertical: 12, borderRadius: parseInt(borderRadius) || 8 }}>
              <Text style={{ color: '#FFFFFF', fontWeight: '600', fontFamily }}>
                {block.config.ctaText || 'Shop Now'}
              </Text>
            </View>
          </View>
        );

        if (backgroundImageUrl) {
          return (
            <ImageBackground
              source={{ uri: backgroundImageUrl }}
              style={{ width: '100%' }}
              imageStyle={{ resizeMode: 'cover' }}
            >
              <View style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}>
                {HeroBannerContent}
              </View>
            </ImageBackground>
          );
        }

        return (
          <View style={{ backgroundColor: themeColors.secondary }}>
            {HeroBannerContent}
          </View>
        );

      case 'PRODUCT_GRID':
        return (
          <View style={{ padding: 24, backgroundColor: themeColors.background }}>
            <Text style={{
              fontSize: parseInt(typography.h2Size) || 24,
              fontWeight: typography.h2Weight as any || '700',
              marginBottom: 16,
              color: themeColors.text,
              fontFamily
            }}>
              {block.config.title || 'Our Products'}
            </Text>

            {/* Search Bar (if enabled) */}
            {block.config.showSearch && (
              <View style={{
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: themeColors.secondary,
                borderRadius: parseInt(borderRadius) || 8,
                paddingHorizontal: 12,
                paddingVertical: 10,
                marginBottom: 16,
                borderWidth: 1,
                borderColor: '#E5E5E5'
              }}>
                <Text style={{
                  fontSize: parseInt(typography.bodySize) || 16,
                  color: themeColors.text,
                  opacity: 0.6,
                  fontFamily,
                  flex: 1
                }}>
                  {block.config.searchPlaceholder || 'Search products...'}
                </Text>
                <Text style={{ fontSize: 20, marginLeft: 8 }}>🔍</Text>
              </View>
            )}

            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
              {[1, 2, 3, 4].map(i => (
                <View key={i} style={{ width: '22%', backgroundColor: themeColors.secondary, borderRadius: parseInt(borderRadius) || 8, padding: 12 }}>
                  <View style={{ width: '100%', height: 120, backgroundColor: '#E0E0E0', borderRadius: parseInt(borderRadius) / 2 || 4, marginBottom: 8 }} />
                  <Text style={{
                    fontSize: parseInt(typography.smallSize) || 14,
                    fontWeight: '600',
                    color: themeColors.text,
                    fontFamily
                  }}>Product {i}</Text>
                  <Text style={{
                    fontSize: parseInt(typography.smallSize) - 2 || 12,
                    color: themeColors.primary,
                    fontFamily
                  }}>₹999</Text>
                </View>
              ))}
            </View>
          </View>
        );

      case 'TEXT_SECTION':
        return (
          <View style={{ padding: 24, backgroundColor: themeColors.background }}>
            <Text style={{
              fontSize: parseInt(typography.h2Size) || 24,
              fontWeight: typography.h2Weight as any || '700',
              marginBottom: 12,
              color: themeColors.text,
              fontFamily
            }}>
              {block.config.heading || 'Section Heading'}
            </Text>
            <Text style={{
              fontSize: parseInt(typography.bodySize) || 16,
              lineHeight: 24,
              color: themeColors.text,
              opacity: 0.8,
              fontFamily
            }}>
              {block.config.content || 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.'}
            </Text>
          </View>
        );

      case 'CONTACT_FORM':
        return (
          <View style={{ padding: 24, backgroundColor: themeColors.background }}>
            <Text style={{
              fontSize: parseInt(typography.h2Size) || 24,
              fontWeight: typography.h2Weight as any || '700',
              marginBottom: 16,
              color: themeColors.text,
              fontFamily
            }}>
              {block.config.title || 'Get In Touch'}
            </Text>
            <View style={{ gap: 12 }}>
              <View style={{ height: 40, backgroundColor: themeColors.secondary, borderRadius: parseInt(borderRadius) || 8, paddingHorizontal: 12, justifyContent: 'center' }}>
                <Text style={{
                  color: themeColors.text,
                  fontSize: parseInt(typography.smallSize) || 14,
                  opacity: 0.6,
                  fontFamily
                }}>Name</Text>
              </View>
              <View style={{ height: 40, backgroundColor: themeColors.secondary, borderRadius: parseInt(borderRadius) || 8, paddingHorizontal: 12, justifyContent: 'center' }}>
                <Text style={{
                  color: themeColors.text,
                  fontSize: parseInt(typography.smallSize) || 14,
                  opacity: 0.6,
                  fontFamily
                }}>Email</Text>
              </View>
              <View style={{ height: 80, backgroundColor: themeColors.secondary, borderRadius: parseInt(borderRadius) || 8, paddingHorizontal: 12, paddingVertical: 12 }}>
                <Text style={{
                  color: themeColors.text,
                  fontSize: parseInt(typography.smallSize) || 14,
                  opacity: 0.6,
                  fontFamily
                }}>Message</Text>
              </View>
              <View style={{ backgroundColor: accentColor, paddingVertical: 12, borderRadius: parseInt(borderRadius) || 8, alignItems: 'center' }}>
                <Text style={{ color: '#FFFFFF', fontWeight: '600', fontFamily }}>Send Message</Text>
              </View>
            </View>
          </View>
        );

      case 'IMAGE_GALLERY':
        return (
          <View style={{ padding: 24, backgroundColor: themeColors.background }}>
            {/* Gallery Title */}
            {block.config.title && (
              <Text style={{
                fontSize: parseInt(typography.h2Size) || 24,
                fontWeight: typography.h2Weight as any || '700',
                marginBottom: 8,
                textAlign: 'center',
                color: themeColors.text,
                fontFamily
              }}>
                {block.config.title}
              </Text>
            )}

            {/* Gallery Subtitle */}
            {block.config.subtitle && (
              <Text style={{
                fontSize: parseInt(typography.bodySize) || 16,
                marginBottom: 24,
                textAlign: 'center',
                color: themeColors.text,
                opacity: 0.7,
                fontFamily
              }}>
                {block.config.subtitle}
              </Text>
            )}

            {/* Gallery Grid */}
            <ImageGalleryPreview
              images={block.config.images || []}
              columns={block.config.columns || 4}
              gap={block.config.gap || 16}
              borderRadius={parseInt(borderRadius) || 8}
              themeColors={themeColors}
            />

            {/* Empty state */}
            {(!block.config.images || block.config.images.length === 0) && (
              <View style={{
                padding: 48,
                backgroundColor: themeColors.secondary,
                borderRadius: parseInt(borderRadius) || 8,
                alignItems: 'center',
                borderWidth: 2,
                borderStyle: 'dashed',
                borderColor: '#E0E0E0'
              }}>
                <Text style={{ fontSize: 48, marginBottom: 12 }}>🖼️</Text>
                <Text style={{
                  fontSize: parseInt(typography.bodySize) || 16,
                  fontWeight: '600',
                  color: themeColors.text,
                  marginBottom: 4,
                  fontFamily
                }}>
                  No images yet
                </Text>
                <Text style={{
                  fontSize: parseInt(typography.smallSize) || 14,
                  color: themeColors.text,
                  opacity: 0.6,
                  textAlign: 'center',
                  fontFamily
                }}>
                  Add images from the Content panel →
                </Text>
              </View>
            )}
          </View>
        );

      case 'SOCIAL_FEED':
        const socialConfig = block.config || {};
        const feeds = socialConfig.feeds || [];
        const feedTitle = socialConfig.title || 'Follow Us';
        const showCaptions = socialConfig.showCaptions !== false;
        const feedColumns = socialConfig.columns || 3;

        return (
          <View style={{
            padding: 32,
            backgroundColor: socialConfig.bgColor || themeColors.background
          }}>
            {/* Title */}
            <Text style={{
              fontSize: parseInt(typography.h2Size) || 24,
              fontWeight: typography.h2Weight || '600',
              color: socialConfig.textColor || themeColors.text,
              textAlign: 'center',
              marginBottom: 32,
              fontFamily
            }}>
              {feedTitle}
            </Text>

            {/* Social Feed Grid */}
            {feeds.length > 0 ? (
              <View style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 16,
                justifyContent: 'center'
              }}>
                {feeds.map((feed: any, index: number) => (
                  <View key={index} style={{
                    width: `calc(${100 / feedColumns}% - 16px)`,
                    minWidth: 250,
                    backgroundColor: themeColors.surface || '#F5F5F5',
                    borderRadius: parseInt(borderRadius) || 8,
                    overflow: 'hidden',
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.1,
                    shadowRadius: 4,
                    elevation: 2
                  }}>
                    {/* Platform Badge */}
                    <View style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: getPlatformColor(feed.platform),
                      paddingHorizontal: 8,
                      paddingVertical: 4,
                      borderRadius: 12,
                      zIndex: 1
                    }}>
                      <Text style={{
                        fontSize: 10,
                        color: '#FFFFFF',
                        fontWeight: '600'
                      }}>
                        {getPlatformIcon(feed.platform)} {feed.platform}
                      </Text>
                    </View>

                    {/* Feed Preview */}
                    <View style={{
                      width: '100%',
                      height: 250,
                      backgroundColor: '#E0E0E0',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {feed.previewUrl ? (
                        <View style={{ width: '100%', height: '100%', position: 'relative' }}>
                          {feed.mediaType === 'video' && (
                            <View style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: [{ translateX: -25 }, { translateY: -25 }],
                              width: 50,
                              height: 50,
                              backgroundColor: 'rgba(0,0,0,0.6)',
                              borderRadius: 25,
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 1
                            }}>
                              <Text style={{ fontSize: 24, color: '#FFFFFF' }}>▶</Text>
                            </View>
                          )}
                          <Text style={{
                            fontSize: 12,
                            color: themeColors.text,
                            textAlign: 'center',
                            padding: 16
                          }}>
                            {feed.mediaType === 'video' ? '🎥' : '📷'} Preview
                          </Text>
                        </View>
                      ) : (
                        <Text style={{
                          fontSize: 40,
                          opacity: 0.3
                        }}>
                          {getPlatformIcon(feed.platform)}
                        </Text>
                      )}
                    </View>

                    {/* Feed Content */}
                    {showCaptions && feed.caption && (
                      <View style={{ padding: 16 }}>
                        <Text style={{
                          fontSize: parseInt(typography.bodySize) || 14,
                          color: themeColors.text,
                          lineHeight: 20,
                          fontFamily
                        }} numberOfLines={3}>
                          {feed.caption}
                        </Text>
                        {feed.link && (
                          <Text style={{
                            fontSize: parseInt(typography.smallSize) || 12,
                            color: themeColors.primary,
                            marginTop: 8,
                            fontFamily
                          }}>
                            View on {feed.platform} →
                          </Text>
                        )}
                      </View>
                    )}
                  </View>
                ))}
              </View>
            ) : (
              <View style={{
                padding: 48,
                backgroundColor: themeColors.surface || '#F5F5F5',
                borderRadius: parseInt(borderRadius) || 8,
                alignItems: 'center'
              }}>
                <Text style={{
                  fontSize: 40,
                  marginBottom: 16
                }}>
                  📱
                </Text>
                <Text style={{
                  fontSize: parseInt(typography.bodySize) || 16,
                  fontWeight: '600',
                  color: themeColors.text,
                  marginBottom: 4,
                  fontFamily
                }}>
                  No social feeds yet
                </Text>
                <Text style={{
                  fontSize: parseInt(typography.smallSize) || 14,
                  color: themeColors.text,
                  opacity: 0.6,
                  textAlign: 'center',
                  fontFamily
                }}>
                  Add social media feeds from the Content panel →
                </Text>
              </View>
            )}
          </View>
        );

      case 'CUSTOM_HTML':
        const customHtmlConfig = block.config || {};
        const htmlContent = customHtmlConfig.html || '';

        return (
          <View style={{
            padding: 24,
            backgroundColor: themeColors.background || '#FFFFFF'
          }}>
            {htmlContent ? (
              <View style={{
                backgroundColor: '#FFFFFF',
                borderRadius: parseInt(borderRadius) || 8,
                overflow: 'hidden',
                borderWidth: 1,
                borderColor: themeColors.secondary || '#E5E5E7'
              }}>
                {/*
                  Sanitized HTML Preview using dangerouslySetInnerHTML.
                  HTML is pre-sanitized by DOMPurify in the RightPanel before being stored,
                  so script injection is already prevented at input time.
                  We use a scoped wrapper class to avoid affecting global styles.
                */}
                <div
                  className="custom-html-block-preview"
                  style={{
                    padding: 16,
                    fontFamily,
                    color: themeColors.text,
                    backgroundColor: themeColors.background || '#FFFFFF',
                    minHeight: 100
                  }}
                  dangerouslySetInnerHTML={{ __html: htmlContent }}
                />

                {/* Metadata Footer */}
                <View style={{
                  padding: 12,
                  backgroundColor: themeColors.surface || '#F5F5F7',
                  borderTopWidth: 1,
                  borderTopColor: themeColors.secondary || '#E5E5E7',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Text style={{
                    fontSize: parseInt(typography.smallSize) || 12,
                    color: themeColors.text,
                    opacity: 0.6,
                    fontFamily
                  }}>
                    🔒 Custom HTML Block (Sanitized)
                  </Text>
                  {customHtmlConfig.lastValidated && (
                    <Text style={{
                      fontSize: parseInt(typography.smallSize) || 12,
                      color: themeColors.text,
                      opacity: 0.5,
                      fontFamily
                    }}>
                      ✅ Validated
                    </Text>
                  )}
                </View>
              </View>
            ) : (
              <View style={{
                padding: 48,
                backgroundColor: themeColors.surface || '#F5F5F5',
                borderRadius: parseInt(borderRadius) || 8,
                alignItems: 'center',
                borderWidth: 2,
                borderColor: themeColors.secondary || '#E5E5E7',
                borderStyle: 'dashed'
              }}>
                <Text style={{
                  fontSize: 48,
                  marginBottom: 16
                }}>
                  🔧
                </Text>
                <Text style={{
                  fontSize: parseInt(typography.bodySize) || 16,
                  fontWeight: '600',
                  color: themeColors.text,
                  marginBottom: 8,
                  fontFamily
                }}>
                  Custom HTML Block
                </Text>
                <Text style={{
                  fontSize: parseInt(typography.smallSize) || 14,
                  color: themeColors.text,
                  opacity: 0.6,
                  textAlign: 'center',
                  fontFamily
                }}>
                  Add your custom HTML content in the Content panel →
                </Text>
              </View>
            )}
          </View>
        );

      default:
        return (
          <View style={{ padding: 24, backgroundColor: themeColors.secondary }}>
            <Text style={{ fontSize: 16, color: themeColors.text, textAlign: 'center', opacity: 0.7 }}>
              {block.type}
            </Text>
          </View>
        );
    }
  };

  const styles = StyleSheet.create({
    container: {
      borderWidth: isSelected ? 2 : 0,
      borderColor: themeColors.accent,
      position: 'relative',
    },
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'transparent',
    },
  });

  return (
    <TouchableOpacity style={styles.container} onPress={onSelect} activeOpacity={0.8}>
      {renderBlockContent()}
      <View style={styles.overlay} />
    </TouchableOpacity>
  );
};

